import { UIElement } from '../ui-element';
import styles from './ui-buttonbar.css';
import { UIButton } from '../button/ui-button';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import { insertElements } from '../../global/render-api';
import { ElementConfig } from '../ui-element.type';
import {
    SlotAssignment,
    UIButtonBarAlignment,
    UIButtonBarPosition,
} from './ui-buttonbar.type';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIButtonbar
 * @element ui-buttonbar
 * @classdesc Represents a class for <code>ui-buttonbar</code> element.
 * Sets buttons to correct position according to brand-book.
 * Buttons have layout types:
 * <ul class="regular-list">
 *   <li><b>primary (default)</b> - primary button with orange background.</li>
 *   <li><b>secondary</b> - secondary button, should always be placed on the right
 *   side in buttonbar</li>
 *   <li><b>tertiary</b> - tertiary button with transparent background, should be on the left side
 *   in most cases.</li>
 * </ul>
 * <br/>
 * To automatically align buttons, use the buttonbar's <b>alignment</b> attribute.
 * To individually align buttons, apply the <b>-left</b> and <b>-right</b> classes to them.
 * @property {UIButtonBarLayout} [layout] {@attr layout} Layout of the buttonbar.
 *  {@desc fluid: Full width container}
 *  {@desc inline: Stays in same line}
 * @property {UIButtonBarAlignment} [alignment] {@attr alignment} Alignment of the buttons in the buttonbar.
 *  {@desc left: All aligned to the left}
 *  {@desc right: All aligned to the right}
 *  {@desc center: All aligned to the center}
 *  {@desc split: Tertiary button(s) aligned to the left, rest to the right}
 *  {@desc vertical: Full width with buttons stacked vertically}
 * @property {HTMLDivElement} leftContainer
 * @property {HTMLDivElement} rightContainer
 * @slot
 * @example
 * <ui-buttonbar alignment="right">
 *   <ui-button label="Submit"></ui-button>
 *   <ui-button class="left" layout="secondary" label="Save"></ui-button>
 *   <ui-button class="right" layout="tertiary" label="Cancel"></ui-button>
 * </ui-buttonbar>
 */
@Component({
    tag: 'ui-buttonbar',
    styles,
})
export class UIButtonbar extends UIElement {
    /** Left slot class list. */
    private static readonly LeftSlotClasses: Array<string> = [
        '-back',
        '-left',
        '-destructive',
        'default-link',
    ];
    /**
     * Right slot class list.
     * (empty string) is a placeholder for unspecified elements
     */
    private static readonly RightSlotClasses: Array<string> = [
        '-guiding',
        '',
        '-right',
        '-next',
    ];

    /**
     * @type {UIButtonBarPosition}
     * @readonly
     */
    static readonly Position = {
        Default: 'default',
        Left: 'left',
        Right: 'right',
    };

    static readonly Alignment: Record<string, UIButtonBarAlignment> = {
        Left: 'left',
        Right: 'right',
        Center: 'center',
        Split: 'split',
        Vertical: 'vertical',
    };

    static readonly AllowedElementsSelectors: string = [
        'ui-button',
        'ui-link',
        'button',
        'a',
        ...['button', 'submit', 'reset'].map((type) => `input[type="${type}"]`),
    ].join(',');

    @Attribute(String) layout?: string;
    @Attribute(String) alignment?: string;

    @Child('.ui-buttonbar__center') centerContainer?: HTMLDivElement;
    @Child('.ui-buttonbar__left') leftContainer!: HTMLDivElement;
    @Child('.ui-buttonbar__right') rightContainer!: HTMLDivElement;

    private observer!: MutationObserver;

    /**
     * Returns the index of the first matching class on the node,
     * or the index of '' if none match.
     * @param {HTMLElement} element
     * @param {Array<string>} classes
     * @returns {number}
     */
    findMatchingClass(element: HTMLElement, classes: Array<string>): number {
        if (element.nodeType !== Node.ELEMENT_NODE) {
            return classes.indexOf('');
        }
        const index = classes.findIndex(
            (c) => c && element.classList.contains(c)
        );
        return index >= 0 ? index : classes.indexOf('');
    }

    private isAttached(el?: HTMLElement): boolean {
        return Boolean(el && this.isParentOf(el));
    }

    /** Returns true if the element has any of the provided classes. */
    private hasMatchingClass(
        element: HTMLElement,
        classes: Array<string>
    ): boolean {
        return classes.some((c) => c && element.classList?.contains(c));
    }

    /**
     * To add button to rendered button bar.
     */
    addButton(element: HTMLElement, position?: UIButtonBarPosition): void {
        if (
            !element ||
            !this.isAttached(this.leftContainer) ||
            !this.isAttached(this.rightContainer)
        ) {
            return;
        }

        if (position === UIButtonbar.Position.Left) {
            this.leftContainer.appendChild(element);
            return;
        }
        if (position === UIButtonbar.Position.Right) {
            this.rightContainer.insertBefore(
                element,
                this.rightContainer.firstChild
            );
            return;
        }

        if (this.hasMatchingClass(element, UIButtonbar.LeftSlotClasses)) {
            this.leftContainer.appendChild(element);
        } else {
            this.rightContainer.insertBefore(
                element,
                this.rightContainer.firstChild
            );
        }
    }

    /**
     * Fires callback when component mutates.
     */
    private handleMutations(): void {
        this.updateHostClasses();
    }

    /**
     * Updates classes on the host element
     */
    private updateHostClasses(): void {
        const allowedSelectors = UIButtonbar.AllowedElementsSelectors;
        const bothContainersEmpty =
            this.querySelectorAll(allowedSelectors).length === 0;
        const leftContainerEmpty =
            !this.leftContainer ||
            this.leftContainer.querySelectorAll(allowedSelectors).length === 0;
        const rightContainerEmpty =
            !this.rightContainer ||
            this.rightContainer.querySelectorAll(allowedSelectors).length === 0;
        this.updateClassList({
            '-empty': bothContainersEmpty,
            '-left-empty': leftContainerEmpty && !bothContainersEmpty,
            '-right-empty': rightContainerEmpty && !bothContainersEmpty,
        });
    }

    private readonly priorityMap: Record<string, number> = {
        [UIButton.Layout.Primary]: 0,
        [UIButton.Layout.Secondary]: 1,
        [UIButton.Layout.Tertiary]: 2,
    };

    /**
     * Retrieves the layout of a given element.
     * @private
     * @param {HTMLElement} element
     * @returns {stirng | null}
     */
    private getLayout(element: HTMLElement): string | null {
        return element.getAttribute?.('layout') || (element as UIButton).layout;
    }

    /**
     * Resolves the priority value of buttonbar elements for sorting.
     * @private
     * @param {HTMLElement} element
     * @returns {number}
     */
    private getPriority(element: HTMLElement): number {
        if (element.localName === 'ui-link') {
            return this.priorityMap[UIButton.Layout.Tertiary];
        }
        return this.priorityMap[
            this.getLayout(element) ?? UIButton.Layout.Primary
        ];
    }

    /**
     * Assigns child nodes to specific slots (left, right, or center) based on buttonbar alignment and button layout.
     * @private
     * @returns An object containing arrays of elements assigned to left, right, and center slots.
     */
    private assignSlots(): SlotAssignment {
        const childNodes = this.detachChildNodes();

        const centerSlot: Array<HTMLElement> = [];
        const leftSlot: Array<HTMLElement> = [];
        const rightSlot: Array<HTMLElement> = [];

        const alignmentMap = {
            [UIButtonbar.Alignment.Left]: leftSlot,
            [UIButtonbar.Alignment.Right]: rightSlot,
            [UIButtonbar.Alignment.Center]: centerSlot,
            [UIButtonbar.Alignment.Vertical]: leftSlot,
        };

        const splitNodes = (elements: Array<HTMLElement>): void => {
            elements.forEach((element) => {
                if (element.localName === 'ui-link') {
                    leftSlot.push(element);
                    return;
                }
                this.getLayout(element) === UIButton.Layout.Tertiary
                    ? leftSlot.push(element)
                    : rightSlot.push(element);
            });
        };

        if (this.alignment) {
            const sortedNodes = childNodes.sort(
                (a, b) => this.getPriority(a) - this.getPriority(b)
            );
            this.alignment === UIButtonbar.Alignment.Split
                ? splitNodes(sortedNodes)
                : alignmentMap[this.alignment].push(...sortedNodes);
        } else {
            childNodes.forEach((node) => {
                const slot = this.hasMatchingClass(
                    node,
                    UIButtonbar.LeftSlotClasses
                )
                    ? leftSlot
                    : rightSlot;
                slot.push(node);
            });
        }

        return { leftSlot, rightSlot, centerSlot };
    }

    /**
     * Sorts buttonbar elements based on the order of their classes in the provided classes array.
     * Classless elements will be placed before those with classes.
     * @private
     * @example
     * If you have four buttons:
     * - Button 1 with class "-right"
     * - Button 2 with no class
     * - Button 3 with class "-next"
     * - Button 4 with no class
     * Sorting them by ['-right', '-next'], result order is: Button 2, Button 4, Button 1, Button 3
     */
    private sortElements(
        elements: Array<HTMLElement>,
        classes: Array<string>
    ): Array<HTMLElement> {
        return elements.sort((a, b) => {
            return (
                this.findMatchingClass(a, classes) -
                this.findMatchingClass(b, classes)
            );
        });
    }

    /**
     * @private
     */
    private getButtonBarContent(): Array<ElementConfig> {
        const split = this.alignment === UIButtonbar.Alignment.Split;

        const { leftSlot, rightSlot, centerSlot } = this.assignSlots();
        let [firstSlot, secondSlot] = split
            ? [rightSlot, leftSlot]
            : [leftSlot, rightSlot];

        if (!this.alignment) {
            firstSlot = this.sortElements(
                firstSlot,
                UIButtonbar.LeftSlotClasses
            );
            secondSlot = this.sortElements(
                secondSlot,
                UIButtonbar.RightSlotClasses
            );
        }

        const elements: Array<ElementConfig> = [
            {
                tagName: 'div',
                classList: {
                    'ui-buttonbar__left': !split,
                    'ui-buttonbar__right': split,
                },
                children: firstSlot,
            },
        ];
        if (this.alignment === UIButtonbar.Alignment.Center) {
            elements.push({
                tagName: 'div',
                classList: { 'ui-buttonbar__center': true },
                children: centerSlot,
            });
        }
        elements.push({
            tagName: 'div',
            classList: {
                'ui-buttonbar__left': split,
                'ui-buttonbar__right': !split,
            },
            children: secondSlot,
        });

        return elements;
    }

    /**
     * @inheritDoc
     */
    render(): void {
        if (
            this.isAttached(this.leftContainer) ||
            this.isAttached(this.rightContainer)
        ) {
            this.updateHostClasses();
            return;
        }
        insertElements(this, this.getButtonBarContent());
        this.updateHostClasses();
    }

    /**
     * @inheritDoc
     */
    hydrate(): void {
        this.observer = new MutationObserver(this.handleMutations.bind(this));
        this.observer.observe(this, { childList: true, subtree: true });
    }

    /**
     * @inheritDoc
     */
    disconnect(): void {
        this.observer.disconnect();
    }

    /**
     * @inheritDoc
     */
    reconnect(): void {
        this.observer.observe(this, { childList: true, subtree: true });
    }
}
